import type {
  WizardConfigType,
  WizardProgressStepType,
  WizardRemoveStepType,
  WizardRemoveStepTypeMap,
  WizardStepToGroupStepMapType,
  WizardStepType,
} from '../components/Wizard/types';

const hasSubStep = (CONFIG: WizardConfigType) => {
  if (!CONFIG) {
    return console.error('ERROR: config is required');
  }

  const tempSubStepMap = new Map();

  // return opposite response
  const hasSubSteps = !CONFIG.every((step) => {
    const { progressStepKey } = step;
    const hasKey = tempSubStepMap.has(progressStepKey);

    if (!hasKey) {
      // set value as an array
      tempSubStepMap.set(progressStepKey, [step]);
    } else {
      // found sub steps (multiple steps with same progress step key)
      const map = tempSubStepMap.get(progressStepKey);
      map.push(step);

      return false;
    }

    return true;
  });

  return hasSubSteps;
};

const getSubStepMap = (CONFIG: WizardConfigType): Map<string, Array<WizardStepType>> => {
  const subStepMap = new Map();

  if (!CONFIG) {
    console.error('ERROR: CONFIG is required');
    return subStepMap;
  }

  CONFIG.forEach((step) => {
    const { progressStepKey } = step;

    const hasKey = subStepMap.has(progressStepKey);

    if (!hasKey) {
      // set value as an array
      subStepMap.set(progressStepKey, [step]);
    } else {
      // found sub steps (multiple steps with same progress step key)
      const map = subStepMap.get(progressStepKey);
      map.push(step);
    }
  });

  // clear anything undefined
  subStepMap.delete(undefined);

  return subStepMap;
};

const getStepToGroupStepMap = (
  CONFIG: WizardConfigType,
  PROGRESS_STEP_CONFIG: Array<WizardProgressStepType>,
): WizardStepToGroupStepMapType => {
  const response: WizardStepToGroupStepMapType = {};

  if (!CONFIG) {
    console.error('ERROR: CONFIG is required');
    return response;
  }

  if (!PROGRESS_STEP_CONFIG) {
    console.error('ERROR: PROGRESS_STEP_CONFIG is required');
    return response;
  }

  CONFIG.forEach((step) => {
    const { progressStepKey, stepKey } = step;

    const { label = '' } = PROGRESS_STEP_CONFIG.find((progressStep) => progressStep.key === progressStepKey) || {};

    response[stepKey] = {
      stepKey,
      progressStepKey,
      label,
    };
  });

  return response;
};

/**
 * Traverse up the CONFIG array given the original step key index and find the closest matching step key index
 */
const findClosestArrayIndexInConfig = ({
  CONFIG,
  ORIGINAL_CONFIG,
  originalStepKeyIndex,
}: {
  CONFIG: WizardConfigType;
  ORIGINAL_CONFIG: Array<WizardStepType>;
  originalStepKeyIndex: number;
}): number => {
  let targetStepKeyIndex = -1;
  const prevOriginalStepKeyIndex = originalStepKeyIndex - 1;

  // can not have a negative prev step
  if (prevOriginalStepKeyIndex === -1) {
    return targetStepKeyIndex;
  }

  const { stepKey: originalPrevStepKey }: WizardStepType = ORIGINAL_CONFIG[prevOriginalStepKeyIndex];

  for (let i = CONFIG.length - 1; i >= 0; i--) {
    // step key from available config
    const { stepKey } = CONFIG[i];

    if (stepKey === originalPrevStepKey) {
      // set matching step key index
      targetStepKeyIndex = i;
      break;
    }
  }

  if (targetStepKeyIndex === -1) {
    // nothing found, try again with the previous index from the original config
    return findClosestArrayIndexInConfig({
      CONFIG,
      ORIGINAL_CONFIG,
      originalStepKeyIndex: prevOriginalStepKeyIndex,
    });
  }

  return targetStepKeyIndex;
};

const addStep = ({
  CONFIG,
  STEP_TO_STEP_GROUP_MAP,
  stepKey,
  removedStepMap,
  ORIGINAL_CONFIG = [],
}: {
  CONFIG: WizardConfigType;
  STEP_TO_STEP_GROUP_MAP: WizardStepToGroupStepMapType;
  stepKey: string;
  removedStepMap: WizardRemoveStepTypeMap;
  ORIGINAL_CONFIG: Array<WizardStepType>;
}):
  | {
      CONFIG: WizardConfigType;
      STEP_TO_STEP_GROUP_MAP: WizardStepToGroupStepMapType;
    }
  | boolean => {
  if (removedStepMap.size === 0) {
    return false;
  }

  const stepKeyIndex = CONFIG.findIndex((step: WizardStepType) => {
    return step.stepKey === stepKey;
  });

  if (stepKeyIndex === -1) {
    const removedStep: WizardRemoveStepType | undefined = removedStepMap.get(stepKey);

    // removedStep might be undefined
    if (!removedStep) {
      return {
        CONFIG,
        STEP_TO_STEP_GROUP_MAP,
      };
    }

    const { config: aConfig, stepToStepGroupMap, originalStepKeyIndex } = removedStep as WizardRemoveStepType;

    // add step as close to the original array index as possible
    const targetStepKeyIndex = findClosestArrayIndexInConfig({ CONFIG, ORIGINAL_CONFIG, originalStepKeyIndex }) + 1;

    // insert into array
    // CONFIG.splice(aStepKeyIndex, 0, aConfig);
    CONFIG.splice(targetStepKeyIndex, 0, aConfig);

    STEP_TO_STEP_GROUP_MAP[stepKey] = stepToStepGroupMap;

    console.info('>> addStep', {
      stepKey,
      stepKeyIndex: targetStepKeyIndex,
    });

    return {
      CONFIG,
      STEP_TO_STEP_GROUP_MAP,
    };
  }

  // keep comment to make debugging easier via toggling
  // console.info('>> Could not add step as it already exists: ', stepKey);
  return false;
};

const removeStep = ({
  CONFIG,
  STEP_TO_STEP_GROUP_MAP,
  removedStepMap,
  stepKey,
  ORIGINAL_CONFIG = [],
}: {
  CONFIG: WizardConfigType;
  STEP_TO_STEP_GROUP_MAP: WizardStepToGroupStepMapType;
  removedStepMap: WizardRemoveStepTypeMap;
  stepKey: string;
  ORIGINAL_CONFIG: Array<WizardStepType>;
}):
  | {
      CONFIG: WizardConfigType;
      STEP_TO_STEP_GROUP_MAP: WizardStepToGroupStepMapType;
    }
  | boolean => {
  const stepKeyIndex = CONFIG.findIndex((step: WizardStepType) => {
    return step.stepKey === stepKey;
  });

  const originalStepKeyIndex = ORIGINAL_CONFIG.findIndex((step: WizardStepType) => {
    return step.stepKey === stepKey;
  });

  if (stepKeyIndex === -1) {
    console.info('>> Could not find step to remove: ', { stepKey, removedStepMap });
    return false;
  }

  if (removedStepMap.has(stepKey)) {
    removedStepMap.delete(stepKey);
  }

  removedStepMap.set(stepKey, {
    originalStepKeyIndex,
    stepToStepGroupMap: {
      ...STEP_TO_STEP_GROUP_MAP[stepKey],
    } as WizardStepToGroupStepMapType,
    config: CONFIG[stepKeyIndex],
  });

  CONFIG.splice(stepKeyIndex, 1);
  delete STEP_TO_STEP_GROUP_MAP[stepKey];

  // keep comment to make debugging easier via toggling
  // console.info('>> removeStep', { stepKey, originalStepKeyIndex, removedStepMap });

  return {
    CONFIG,
    STEP_TO_STEP_GROUP_MAP,
  };
};

export { addStep, getStepToGroupStepMap, getSubStepMap, hasSubStep, removeStep };
